import * as React from 'react'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableContainer from '@mui/material/TableContainer'
import TableRow from '@mui/material/TableRow'
import { Autocomplete, Checkbox, CircularProgress, Stack, Typography, useTheme } from '@mui/material'
import { Order, HeadCell } from '../../../utils/tableFunctions'
import TableButtonDelete from '../../table/TableButtonDelete'
import EnhancedTableHead from '../../table/EnhancedTableHead'
import CustomTextField from '../../common/CustomTextField'
import { ContractorBriefDto, ContractorOfEventDto, ContractorType } from '../../../services/api/sharedTypes/Contractor'
import { EventDto } from '../../../services/api/sharedTypes/Event'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import {
    convertContractorOfEventDtoToContractorBriefDto,
    findNameOfContractorAssignedToDifferentEventById,
} from '../../../utils/contractor-utils'
import MarkChatReadRoundedIcon from '@mui/icons-material/MarkChatReadRounded'
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded'
import MonetizationOnRoundedIcon from '@mui/icons-material/MonetizationOnRounded'
import CustomTableCell from '../../common/CustomTableCell'
import { useAppDispatch } from '../../../hooks/reduxHooks'
import { displayMessage } from '../../../services/redux/snackbar/snackbar-actions'
import { IdNameDto } from '../../../services/api/sharedTypes/Common'
import SmallPlusButton from '../../common/SmallPlusButton'

const headCells: readonly HeadCell[] = [
    {
        id: 'contractorName',
        numeric: false,
        disablePadding: false,
        label: 'Dopravce',
    },
    {
        id: 'costs',
        numeric: true,
        disablePadding: false,
        label: 'Náklady',
    },
    {
        id: 'hasReacted',
        numeric: false,
        disablePadding: false,
        label: 'Brigádník reagoval',
        component: <MarkChatReadRoundedIcon />,
        centered: true,
    },
    {
        id: 'isConfirmed',
        numeric: false,
        disablePadding: false,
        label: 'Potvrzení brigádníka',
        component: <CheckCircleOutlineRoundedIcon />,
        centered: true,
    },
    {
        id: 'gotPaid',
        numeric: false,
        disablePadding: false,
        label: 'Dostal zaplaceno',
        component: <MonetizationOnRoundedIcon />,
        centered: true,
    },
    {
        id: 'buttons',
        numeric: false,
        disablePadding: false,
        label: '',
    },
]
interface CarrierTableProps {
    contractors: IdNameDto[]
    contractorList: ContractorBriefDto[]
    isContractorListLoading: boolean
    handleSelectContractorChange: (
        newValue: IdNameDto,
        oldId: string,
        index: number,
        sortBy: keyof ContractorOfEventDto | undefined,
        sortOrder: 'asc' | 'desc'
    ) => void
    event: EventDto
    setEvent: (value: React.SetStateAction<EventDto | undefined>) => void
    /**
     * onValueChanged is function called when the user changes some value in the table
     * @param value true if contractor was changed, false otherwise
     */
    onValueChanged?: (value: React.SetStateAction<boolean>) => void
}

const CarrierTable: React.FC<CarrierTableProps> = ({
    contractors,
    contractorList,
    isContractorListLoading,
    event,
    setEvent,
    handleSelectContractorChange,
    onValueChanged,
}: CarrierTableProps) => {
    const theme = useTheme()
    const dispatch = useAppDispatch()

    const [order, setOrder] = React.useState<Order>('desc')
    const [orderBy, setOrderBy] = React.useState<keyof ContractorOfEventDto>()

    const carriers = event.contractors
        .filter((contractor) => contractor.contractorType === ContractorType.Carrier ?? [])
        .sort((a: ContractorOfEventDto, b: ContractorOfEventDto) => {
            if (orderBy) {
                if ((a[orderBy] ?? 0) < (b[orderBy] ?? 0)) {
                    return order === 'asc' ? -1 : 1
                }
                if ((a[orderBy] ?? 0) > (b[orderBy] ?? 0)) {
                    return order === 'asc' ? 1 : -1
                }
            }
            return 0
        })

    const carriersInDifferentEvent = React.useMemo(
        () =>
            carriers
                .map((contractor) => {
                    return contractor.contractorId
                        ? findNameOfContractorAssignedToDifferentEventById(
                            contractor.contractorId,
                            event.id,
                            event.startsAt,
                            event.endsAt,
                            contractorList
                        )
                        : null
                })
                .filter((contractor) => contractor !== null),
        [carriers, event.id, event.startsAt, event.endsAt, contractorList]
    )

    const handleRequestSort = (event: React.MouseEvent<unknown>, property: string) => {
        const isAsc = orderBy === property && order === 'asc'
        setOrder(isAsc ? 'desc' : 'asc')
        setOrderBy(property as keyof ContractorOfEventDto)
    }

    return (
        <>
            <Stack direction='row' justifyContent='space-between' mb={2}>
                <Typography variant='h6' color='primary'>
                    Dopravce
                </Typography>
                <SmallPlusButton onClick={() => {
                    const isThereEmptyContractor = event.contractors.find((contractor) => contractor.contractorId === '')
                    if (isThereEmptyContractor)
                        return dispatch(displayMessage({ message: 'Nejdříve zvolte prázdného dopravce', type: 'warning' }))

                    setEvent({
                        ...event,
                        contractors: [
                            ...event.contractors,
                            {
                                contractorId: '',
                                costs: 0,
                                isLeader: false,
                                hasResponded: false,
                                isConfirmed: false,
                                gotPaid: false,
                                contractorName: '',
                                contractorType: ContractorType.Carrier,
                            },
                        ],
                    })
                }} />
            </Stack>
            <Box style={{ width: '100%' }}>
                <TableContainer>
                    <Table size='small'>
                        <EnhancedTableHead
                            order={order}
                            orderBy={orderBy ?? ''}
                            onRequestSort={handleRequestSort}
                            rowCount={carriers.length}
                            headCells={headCells}
                        />
                        <TableBody>
                            {carriers.map((row, index: number) => {
                                return (
                                    <TableRow
                                        tabIndex={-1}
                                        key={index}
                                        style={{
                                            height: '42px',
                                            backgroundColor: row.isConfirmed ? theme.palette.tableRow?.background.paid : '',
                                        }}
                                    >
                                        <CustomTableCell>
                                            {contractors ? (
                                                <Autocomplete
                                                    options={contractors
                                                        .filter(
                                                            (contractor) =>
                                                                !carriers
                                                                    .filter(
                                                                        (localContractor) =>
                                                                            localContractor.contractorType ===
                                                                            ContractorType.Carrier
                                                                    )
                                                                    .some(
                                                                        (localContractor) =>
                                                                            localContractor.contractorId === contractor.id
                                                                    )
                                                        )
                                                        .sort((a, b) => {
                                                            if (a.name > b.name) return 1
                                                            if (a.name < b.name) return -1
                                                            return 0
                                                        })}
                                                    getOptionLabel={(option) =>
                                                        option.name === 'Null' ? '' : option.name
                                                    }
                                                    value={
                                                        contractors.find((contractor) => contractor.id === row.contractorId) ??
                                                        convertContractorOfEventDtoToContractorBriefDto(
                                                            carriers.filter(
                                                                (localContractor) =>
                                                                    localContractor.contractorType === ContractorType.Carrier
                                                            )[index]
                                                        ) ??
                                                        null
                                                    }
                                                    size='small'
                                                    onChange={(eventChange, newValue) => {
                                                        if (newValue === null) return
                                                        handleSelectContractorChange(
                                                            newValue,
                                                            row.contractorId ?? '',
                                                            index,
                                                            orderBy,
                                                            order
                                                        )
                                                        onValueChanged && onValueChanged(true)
                                                    }}
                                                    renderInput={(params) => (
                                                        <CustomTextField
                                                            {...params}
                                                            size='small'
                                                            sx={{ minWidth: '170px' }}
                                                            isSmall
                                                            textSize={14}
                                                            isPaddingDisabled
                                                        />
                                                    )}
                                                />
                                            ) : (
                                                <Typography variant='body1' color='error'>
                                                    Chyba při načítání dopravců
                                                </Typography>
                                            )}
                                        </CustomTableCell>
                                        <CustomTableCell width={84}>
                                            <CustomTextField
                                                value={row.costs || ''}
                                                size='small'
                                                isSmall
                                                style={{ width: '69px' }}
                                                onChange={(e) => {
                                                    if (isNaN(Number(e.target.value))) return
                                                    onValueChanged && onValueChanged(true)
                                                    setEvent({
                                                        ...event,
                                                        contractors: event.contractors.map((contractor) => {
                                                            if (contractor.contractorId === row.contractorId) {
                                                                return {
                                                                    ...contractor,
                                                                    costs: Number(e.target.value),
                                                                }
                                                            }
                                                            return contractor
                                                        }),
                                                    })
                                                }}
                                            />
                                        </CustomTableCell>
                                        <CustomTableCell center width={70}>
                                            <Checkbox disabled checked={row.hasResponded ? true : false} />
                                        </CustomTableCell>
                                        <CustomTableCell center width={70}>
                                            <Checkbox
                                                checked={row.isConfirmed ? true : false}
                                                onChange={(e) => {
                                                    setEvent({
                                                        ...event,
                                                        contractors: event.contractors.map((contractor) => {
                                                            if (contractor.contractorId === row.contractorId) {
                                                                return {
                                                                    ...contractor,
                                                                    isConfirmed: e.target.checked,
                                                                }
                                                            }
                                                            return contractor
                                                        }),
                                                    })
                                                    onValueChanged && onValueChanged(true)
                                                }}
                                            />
                                        </CustomTableCell>
                                        <CustomTableCell center width={70}>
                                            <Checkbox
                                                checked={row.gotPaid ? true : false}
                                                onChange={(e) => {
                                                    setEvent({
                                                        ...event,
                                                        contractors: event.contractors.map((contractor) => {
                                                            if (contractor.contractorId === row.contractorId) {
                                                                return {
                                                                    ...contractor,
                                                                    gotPaid: e.target.checked,
                                                                }
                                                            }
                                                            return contractor
                                                        }),
                                                    })
                                                    onValueChanged && onValueChanged(true)
                                                }}
                                            />
                                        </CustomTableCell>
                                        <CustomTableCell width={48} align='right'>
                                            <Stack spacing={2} direction='row' justifyContent='end'>
                                                <TableButtonDelete
                                                    nameOfEntityInDialogMessage={row.contractorName}
                                                    onClick={() =>
                                                        setEvent({
                                                            ...event,
                                                            contractors: event.contractors.filter(
                                                                (c) => c.contractorId !== row.contractorId
                                                            ),
                                                        })
                                                    }
                                                />
                                            </Stack>
                                        </CustomTableCell>
                                    </TableRow>
                                )
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
                {
                    isContractorListLoading ? (
                        <CircularProgress size={20} />
                    ) :
                        carriersInDifferentEvent.length > 0 ? (
                            <Stack direction='row' spacing={theme.spacing(1)} alignItems='center'>
                                <Typography variant='body2' color={theme.palette.warning.dark}>
                                    <WarningAmberOutlinedIcon />
                                </Typography>
                                <Typography variant='body2' color={theme.palette.warning.dark}>
                                    {carriersInDifferentEvent.map(
                                        (carrier, index) =>
                                            carrier && (
                                                <span key={index}>
                                                    {carrier.name + ' (' + carrier.events.map((event) => event.eventName) + ')'}
                                                    {carriersInDifferentEvent.length > 1 &&
                                                        carriersInDifferentEvent.indexOf(carrier) !== carriersInDifferentEvent.length - 1 ? (
                                                        <span>, </span>
                                                    ) : (
                                                        ' '
                                                    )}
                                                </span>
                                            )
                                    )}
                                    <span>je ve stejné časové období přiřazen/a na jinou událost.</span>
                                </Typography>
                            </Stack>
                        ) : (
                            <></>
                        )}
                {
                    carriers.length > 0 && (
                        <Typography variant='caption'>Upozornění: Nedoporučujeme mazat již schváleného dopravce.</Typography>

                    )
                }
            </Box>
        </>
    )
}

export default CarrierTable
